import type { FirebaseOptions } from 'firebase/app';

/**
 * Firebase web client config (public values — security is enforced by Rules / backend).
 * CRA injects `process.env.REACT_APP_*` at build time.
 *
 * Fallbacks match historical `src/index.tsx` defaults so local dev works without a `.env` file.
 * Production builds should set env vars explicitly.
 */
export function getFirebaseWebConfig(): FirebaseOptions {
    return {
        apiKey:
            process.env.REACT_APP_FIREBASE_API_KEY ??
            'AIzaSyDm7ACTSv70Rw90P-iFiak8A9z1d5KqnxQ',
        authDomain:
            process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ?? 'vehiwheel.firebaseapp.com',
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ?? 'vehiwheel',
        storageBucket:
            process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ?? 'vehiwheel.appspot.com',
        messagingSenderId:
            process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ?? '881728558027',
        appId:
            process.env.REACT_APP_FIREBASE_APP_ID ??
            '1:881728558027:web:ea5432d420cb2108acdc4e',
        measurementId:
            process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ?? 'G-3F6C1FGEVX',
    };
}

/** Secondary Firestore database id (not `(default)`). */
export const FIRESTORE_DB_TEST_ID =
    process.env.REACT_APP_FIRESTORE_DB_TEST ?? 'test';

/** QA knowledge-base Firestore database id (`vehiwheelqa`). */
export const FIRESTORE_DB_QUESTION_BANK_ID =
    process.env.REACT_APP_FIRESTORE_DB_QA ?? 'vehiwheelqa';

/**
 * Standalone test Firestore database (`vehiwheeltestdata`): `common` + `questions_cl` only.
 * Override with `REACT_APP_FIRESTORE_DB_TESTDATA` if the Console id differs.
 */
export const FIRESTORE_DB_TESTDATA_QUESTION_BANK_ID = (
    process.env.REACT_APP_FIRESTORE_DB_TESTDATA ?? 'vehiwheeltestdata'
).trim();

/**
 * Production question bank Firestore **database id** (named database in Firebase Console).
 * When empty, `dbQuestionBankProd` is not created and migration UI must not target prod.
 */
export const FIRESTORE_DB_PROD_QUESTION_BANK_ID = (
    process.env.REACT_APP_FIRESTORE_DB_PROD ?? ''
).trim();

/**
 * Preprod / prod-prep question bank (`vehiwheelprodv2`) — Prod→Prod v2 pipeline + Test Catalog trees.
 * When empty, prodv2 is omitted from selectors.
 */
export const FIRESTORE_DB_PRODV2_QUESTION_BANK_ID = (
    process.env.REACT_APP_FIRESTORE_DB_PRODV2 ?? ''
).trim();

/**
 * Normalize env bucket strings for `getStorage(app, 'gs://…')`.
 * Uses the **exact** Cloud Storage bucket name (as in Firebase Console → Storage dropdown / `gs://…`).
 * Strips an optional `gs://` prefix only — does **not** append `.appspot.com` (named buckets are often
 * `vehiwheelprod`, while the default app bucket is `project.appspot.com`; treating those the same breaks uploads).
 */
export function normalizeFirebaseStorageBucketIdForGs(raw: string): string {
    let t = String(raw ?? '').trim();
    if (!t) return '';
    if (t.toLowerCase().startsWith('gs://')) {
        t = t.slice('gs://'.length).trim();
    }
    return t;
}

/**
 * Firebase Storage bucket for production question images (paired with prod question bank Firestore DB).
 * Must match the bucket id shown in Firebase Console (e.g. `vehiwheelprod`, `*.appspot.com`, or `*.firebasestorage.app`).
 * When empty, `getFirebaseStorageForQuestionImages('vehiwheelprod')` throws if called.
 */
export const FIREBASE_STORAGE_BUCKET_PROD = normalizeFirebaseStorageBucketIdForGs(
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_PROD ?? ''
);

/**
 * Optional Storage bucket for {@link FIRESTORE_DB_PRODV2_QUESTION_BANK_ID}.
 * When empty, image uploads for prodv2 reuse {@link FIREBASE_STORAGE_BUCKET_PROD} (shared URLs OK for prep).
 */
export const FIREBASE_STORAGE_BUCKET_PRODV2 = normalizeFirebaseStorageBucketIdForGs(
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_PRODV2 ?? ''
);

/**
 * Firebase Storage bucket for the QA question image library (`questionImages` in Firestore `vehiwheelqa`).
 * Download URLs look like `.../v0/b/<this-id>/o/...` — set {@link process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_QA}
 * to that exact segment if it differs from the default (e.g. `vehiwheelqa.appspot.com` vs `vehiwheelqa`).
 * Default DB continues using {@link getFirebaseWebConfig}.storageBucket.
 */
export const FIREBASE_STORAGE_BUCKET_QA =
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_QA ?? 'vehiwheelqa';

/**
 * Storage bucket paired with {@link FIRESTORE_DB_TESTDATA_QUESTION_BANK_ID} (`questionImages` + mirrored URLs).
 */
export const FIREBASE_STORAGE_BUCKET_TESTDATA = normalizeFirebaseStorageBucketIdForGs(
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_TESTDATA ?? 'vehiwheeltestdata'
);
